import { CustomButton } from "@/components/ui/custom-button";
import { AnimatedWords } from "@/components/ui/animated-words";
import { Link } from "@/i18n/navigation";
import { motion, AnimatePresence } from "motion/react";
import { useEffect, useState } from "react";
import { DemoModal } from "@/components/shared/DemoModal";
import { WaitlistFormModal } from "@/components/modals/FormModal";

interface AnimatedWord {
    words: string[];
    interval?: number;
    colors?: string[];
}

interface HeroSectionProps {
    desktopImage?: string;
    mobileImage?: string;
    desktopImageAlt?: string;
    mobileImageAlt?: string;
    heading?: string | React.ReactNode;
    description?: string;
    mobileDescription?: string;
    animatedWords?: AnimatedWord[];
    animatedWordsStyle?: "sm" | "md" | "lg" | "xl";
    buttonText?: string;
    buttonHref?: string;
    showDemoButton?: boolean;
}

export function HeroSection({
    desktopImage = "/apple_images.png",
    mobileImage = "/apple_image_mobile.png",
    desktopImageAlt = "StudyStash Dashboard shown on a MacBook Pro, iPad and iPhone",
    mobileImageAlt = "StudyStash Dashboard shown on a MacBook Pro",
    heading = "The only adaptive learning intelligence built on proven neuroscience",
	description,
    mobileDescription,
    animatedWords = [],
    animatedWordsStyle = "sm",
    buttonText = "Join the Waitlist",
    buttonHref = "/register",
    showDemoButton = true
}: HeroSectionProps) {
    const [mounted, setMounted] = useState(false);
    const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
    const [currentIndices, setCurrentIndices] = useState<number[]>(new Array(animatedWords.length).fill(0));

    useEffect(() => {
        setMounted(true);
        
        // Create intervals for each animated word group
        const intervals = animatedWords.map((wordGroup, index) => {
            return setInterval(() => {
                setCurrentIndices(prev => {
                    const newIndices = [...prev];
                    newIndices[index] = (newIndices[index] + 1) % wordGroup.words.length;
                    return newIndices;
                });
            }, wordGroup.interval || 3000);
        });

        return () => intervals.forEach(clearInterval);
    }, [animatedWords]);

	return (
		<div className="mt-8 md:mt-16 md:my-16 flex w-full relative">
			{/* Background SVG - Positioned relative to section */}
			<div className="absolute inset-0 z-0 pointer-events-none">
				<img 
					src="/top_line.svg" 
					alt="Illustration of the StudyStash teaching-learning loop" 
					className="absolute w-full h-[800px] md:h-[1000px] object-cover opacity-100"
					style={{
						top: '-10%',
						left: '0%',
						transform: 'scale(1.0)',
						objectPosition: 'left center'
					}}
				/>
			</div>
			<div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
				<div className="relative flex flex-col lg:flex-row lg:items-start">
					{/* Left side - Text content */}
					<div className="flex flex-col lg:w-[70%] lg:pr-8">
                        {/* Small style: Show animated words above the heading */}
                        {/* {animatedWords.length > 0 && animatedWordsStyle === "sm" && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex flex-wrap gap-2 mb-2 -mt-4 md:-mt-8"
                            >
                                {animatedWords.map((wordGroup, index) => (
                                    <AnimatedWords
                                        key={index}
                                        words={wordGroup.words}
                                        interval={wordGroup.interval}
                                        colors={wordGroup.colors}
                                        variant="primary"
                                        size={animatedWordsStyle}
                                    />
                                ))}
                            </motion.div>
                        )} */}

						<div className="flex flex-col justify-start pb-4 md:pb-8 text-left leading-[40px] sm:leading-[50px] lg:leading-[70px] md:leading-[60px]">
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: animatedWordsStyle === "sm" ? 0.2 : 0 }}
                            className="text-[28px] sm:text-[35px] font-medium text-gray-800 lg:text-[64px] md:text-[55px]"
                        >
                            {animatedWords.length > 0 && animatedWordsStyle !== "sm" ? (
                                <span>
                                    {(() => {
                                        const parts = heading?.toString().split(/\[([^\]]+)\]/).map((part, index) => {
                                            if (index === 2) {
                                                // This is the part after the animated word - put it on a new line
                                                return <div key={index} className="block mt-2">{part}</div>;
                                            }
                                            return <span key={index}>{part}</span>;
                                        });
                                        return (
                                            <>
                                                {parts?.map((part, partIndex) => {
                                                    if (Array.isArray(part)) {
                                                        return part;
                                                    }
                                                    if (partIndex % 2 === 1) {
                                                        // This is the animated word part - use new AnimatedWords component with large size
                                                        const wordGroup = animatedWords[0];
                                                        return (
                                                            <span key={partIndex} className="relative inline-block">
                                                                <AnimatedWords
                                                                    words={wordGroup.words}
                                                                    interval={wordGroup.interval}
                                                                    colors={wordGroup.colors}
                                                                    variant="primary"
                                                                    size={animatedWordsStyle}
                                                                    className="font-medium!"
                                                                />
                                                            </span>
                                                        );
                                                    }
                                                    return part;
                                                })}
                                            </>
                                        );
                                    })()}
                                </span>
                            ) : (
                                // For small style or no animated words, show the clean heading without brackets
                                animatedWords.length > 0 && animatedWordsStyle === "sm" 
                                    ? heading?.toString().replace(/\[([^\]]+)\]/g, '$1') // Remove brackets but keep the text
                                    : heading
                            )}
                        </motion.h1>
						</div>
                        {description && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                                className="hidden max-w-xl text-left sm:flex sm:flex-col"
                            >
                                <p>
                                    {description}
                                </p>
                            </motion.div>
                        )}
                        {mobileDescription && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                                className="flex max-w-3xl text-left sm:hidden"
                            >
                                <p>
                                    {mobileDescription}
                                </p>
                            </motion.div>
                        )}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.6 }}
							className="flex items-start space-x-2 pt-4 md:pt-8"
						>
							{buttonHref === "/register" ? (
								<CustomButton 
									variant="primary" 
									size="lg" 
									onClick={() => setIsWaitlistModalOpen(true)}
								>
									{buttonText}
								</CustomButton>
							) : (
								<Link href={buttonHref}>
									<CustomButton 
										variant="primary" 
										size="lg"
									>
										{buttonText}
									</CustomButton>
								</Link>
							)}
							{showDemoButton && (
								<DemoModal type="xl" />
							)}
						</motion.div>
					</div>
					
					{/* Right side - Images */}
					<div className="lg:w-[65%] lg:absolute lg:bottom-[-200px] lg:right-0 relative z-10 mt-6 lg:mt-0">
						<motion.img
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.8 }}
							src={desktopImage}
							alt={desktopImageAlt}
							className="hidden w-full max-w-[900px] object-contain pt-6 lg:pt-0 sm:flex relative z-10"
						/>
						<motion.img
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.8 }}
							src={mobileImage}
							alt={mobileImageAlt}
							className="flex w-full max-w-[500px] mx-auto object-contain pt-6 lg:pt-0 sm:hidden relative z-10"
						/>
					</div>
				</div>
			</div>
			<WaitlistFormModal
				open={isWaitlistModalOpen}
				onOpenChange={setIsWaitlistModalOpen}
			/>
		</div>
	);
}
